import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Select,
  Switch,
  Image,
  Box,
  Spinner,
  Alert,
  AlertIcon,
  useDisclosure,
  Textarea,
} from '@chakra-ui/react';
import { FiTrash2, FiUserPlus, FiEdit2, FiSave, FiX, FiUnlock, FiLock, FiInfo, FiRefreshCw, FiMapPin } from 'react-icons/fi';
import { useForm } from 'react-hook-form';
import UserPicker, { User as AutocompleteUser } from '@/components/common/UserPicker';
import LocationPicker, { LocationData } from '@/components/common/LocationPicker';
import apiClient, { Device, User, Tenant } from '@/services/letsparkyClient';
import { EntityStatus } from '@letsparky/api-v2-client';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';

// RelationshipType enum
export enum RelationshipType {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  USERWITHSHARE = 'USERWITHSHARE',
  USER = 'USER',
  VISITOR = 'VISITOR'
}

interface DeviceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: Device | null;
  onDeviceUpdated: () => void;
}

interface DeviceFormData {
  nick_name: string;
  notes?: string;
  location?: string;
  description?: string;
  status: string;
  sensor?: boolean;
  photo?: string;
  geo_lat?: number;
  geo_lng?: number;
}

interface QrDataFormData {
  qr_data: string;
  device_code: string;
}

interface AssignedUser {
  id: string;
  name?: string;
  surname?: string;
  email?: string;
  phone_number?: string;
  relationship_type?: string;
  is_favorite?: boolean;
  access_granted_at?: string;
}

const DeviceDetailsModal: React.FC<DeviceDetailsModalProps> = ({
  isOpen,
  onClose,
  device,
  onDeviceUpdated,
}) => {
  const toast = useToast();
  const { token, user } = useAuthStore();
  const isSystem = user?.role === 'SYSTEM';
  
  const [isEditingDevice, setIsEditingDevice] = useState(false);
  const [isEditingQrData, setIsEditingQrData] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingAssignedUsers, setIsLoadingAssignedUsers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AutocompleteUser | null>(null);
  const [selectedRelationshipType, setSelectedRelationshipType] = useState<RelationshipType>(RelationshipType.USER);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const { t } = useTranslation();

  const {
    register: registerDevice,
    handleSubmit: handleSubmitDevice,
    reset: resetDevice,
    formState: { errors: deviceErrors },
  } = useForm<DeviceFormData>();

  const {
    register: registerQrData,
    handleSubmit: handleSubmitQrData,
    reset: resetQrData,
    formState: { errors: qrDataErrors },
  } = useForm<QrDataFormData>();

  // Reset forms when device changes
  useEffect(() => {
    if (device) {
      resetDevice({
        nick_name: device.nick_name || '',
        description: device.description || '',
        notes: device.notes || '',
        location: device.location || '',
        photo: device.photo || '',
      });
      resetQrData({
        qr_data: device.qr_data || '',
      });
      // Initialize location data from device coordinates
      if (device.geo_lat && device.geo_lng) {
        setLocationData({
          latitude: device.geo_lat,
          longitude: device.geo_lng,
          address: device.location || undefined,
        });
      } else {
        setLocationData(null);
      }
      fetchAssignedUsers();
    }
  }, [device, resetDevice, resetQrData]);

  useEffect(() => {
    if (isOpen && isSystem) fetchAvailableTenants();
  }, [isOpen]);
  
  const fetchAvailableTenants = async () => {
    if (!token || !isSystem) return;
    setIsLoadingTenants(true);
    try {
      apiClient.client.setToken(token);
      const resp = await apiClient.tenants.list({ limit: 4000 });
      setAvailableTenants(resp.data?.data ?? []);
    } catch (err) {
      console.error('Error fetching tenants:', err);
    } finally {
      setIsLoadingTenants(false);
    }
  };
  
  const handleAssignTenant = async () => {
    if (!device || !token || !selectedTenantId) return;
    setIsSaving(true);
    try {
      apiClient.client.setToken(token);
      await apiClient.devices.assignToTenant(device.id, {
        tenant_id: selectedTenantId,
      });
      toast({
        title: 'Tenant assigned successfully',
        status: 'success',
        duration: 3000,
      });
      setSelectedTenantId('');
      onDeviceUpdated();            // refresh parent data
    } catch (err) {
      toast({
        title: 'Error assigning tenant',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };
  // Fetch available users for assignment
  useEffect(() => {
    if (isOpen && device) {
      fetchAvailableUsers();
    }
  }, [isOpen, device]);

  const fetchAssignedUsers = async () => {
    if (!device || !token) return;
    
    setIsLoadingAssignedUsers(true);
    try {
      apiClient.client.setToken(token);
      const response = await apiClient.devices.getAssignedUsers(device.id);
      setAssignedUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching assigned users:', error);
    } finally {
      setIsLoadingAssignedUsers(false);
    }
  };

  const fetchAvailableUsers = async () => {
    if (!token) return;
    
    setIsLoadingUsers(true);
    try {
      apiClient.client.setToken(token);
      
      // Fetch users based on user role
      const params = isSystem ? {limit: 2000, sort: 'updated_at', order: 'desc' as const } : { page: 1,limit: 2000, tenant_id: user?.tenantId, sort: 'updated_at', order: 'desc' as const };
      const response = await apiClient.users.listPaginated(params);
      setAvailableUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching available users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleLocationChange = (location: LocationData) => {
    setLocationData(location);
  };

  const handleDeviceUpdate = async (data: DeviceFormData) => {
    if (!device || !token) return;
    
    setIsSaving(true);
    try {
      apiClient.client.setToken(token);
      await apiClient.devices.update(device.id, {
        nick_name: data.nick_name,
        location: locationData?.address || data.location,
        photo: data.photo,
        description: data.description ?? '',
        notes: data.notes ?? '',
        geo_lat: locationData?.latitude,
        geo_lng: locationData?.longitude,
      });
      
      toast({
        title: 'Device updated successfully',
        status: 'success',
        duration: 3000,
      });
      
      setIsEditingDevice(false);
      onDeviceUpdated();
    } catch (error) {
      toast({
        title: 'Error updating device',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleQrDataSave = async (data: QrDataFormData) => {
    if (!device || !token) return;
    
    setIsSaving(true);
    try {
      apiClient.client.setToken(token);
      
      const hasExistingQrData = !!device.qr_data;
      
      if (hasExistingQrData) {
        // Update existing QR data
        await apiClient.devices.updateQrData(device.id, {
          qr_data: data.qr_data,
          device_code: data.device_code
        });
      } else {
        // Create new QR data
        await apiClient.devices.createQrData(device.id, {
          qr_data: data.qr_data,
          device_code: data.device_code
        });
      }
      
      toast({
        title: hasExistingQrData ? t('device.qr_data_updated') : t('device.qr_data_created'),
        status: 'success',
        duration: 3000,
      });
      
      setIsEditingQrData(false);
      onDeviceUpdated();
    } catch (error) {
      toast({
        title: t('device.error_saving_qr_data'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleQrDataDelete = async () => {
    if (!device || !token) return;
    
    if (!window.confirm(t('device.confirm_delete_qr_data'))) {
      return;
    }
    
    setIsSaving(true);
    try {
      apiClient.client.setToken(token);
      await apiClient.devices.removeQrData(device.id);
      
      toast({
        title: t('device.qr_data_deleted'),
        status: 'success',
        duration: 3000,
      });
      
      setIsEditingQrData(false);
      onDeviceUpdated();
    } catch (error) {
      toast({
        title: t('device.error_deleting_qr_data'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssignUser = async () => {
    if (!device || !token || !selectedUser) return;
    
    setIsSaving(true);
    try {
      apiClient.client.setToken(token);
      await apiClient.devices.assignToUser(device.id, {
        user_id: selectedUser.id,
        relationship_type: selectedRelationshipType,
      });
      
      toast({
        title: 'User assigned successfully',
        status: 'success',
        duration: 3000,
      });
      
      setSelectedUser(null);
      setSelectedRelationshipType(RelationshipType.USER);
      fetchAssignedUsers();
    } catch (error) {
      toast({
        title: 'Error assigning user',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUserSelect = (user: AutocompleteUser | null) => {
    setSelectedUser(user);
  };

  const handleRevokeUser = async (userId: string) => {
    if (!device || !token) return;
    
    if (!window.confirm('Are you sure you want to revoke access for this user?')) {
      return;
    }
    
    try {
      apiClient.client.setToken(token);
      await apiClient.devices.revokeUserAccess(device.id, userId);
      
      toast({
        title: 'User access revoked successfully',
        status: 'success',
        duration: 3000,
      });
      
      fetchAssignedUsers();
    } catch (error) {
      toast({
        title: 'Error revoking user access',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeviceCommand = async (command: 'unblock' | 'block' | 'info' | 'reboot') => {
    if (!device || !token) return;
    
    setIsSaving(true);
    try {
      apiClient.client.setToken(token);
      
      switch (command) {
        case 'unblock':
          await apiClient.devices.unblock(device.id);
          break;
        case 'block':
          await apiClient.devices.block(device.id);
          break;
        case 'info':
          await apiClient.devices.getInfo(device.id);
          break;
        case 'reboot':
          await apiClient.devices.reboot(device.id);
          break;
      }
      toast({
        title: `Command "${command}" sent`,
        description: `${command} command has been sent to device ${device.nick_name || device.serial_no}`,
        status: 'success',
        duration: 3000,
      });      
    } catch (error) {
      toast({
        title: `Error sending ${command} command`,
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!device) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'green';
      case 'offline':
        return 'red';
      case 'maintenance':
        return 'orange';
      default:
        return 'gray';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent maxHeight="90vh" overflowY="auto">
        <ModalHeader>
          Device Details: {device.nick_name || device.serial_no}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <Tabs>
          <TabList>
            <Tab>{t('device.info')}</Tab>
            <Tab>{t('device.qr_data')}</Tab>
            <Tab>{t('device.assigned_users')}</Tab>
            <Tab>{t('device.commands')}</Tab>
            {isSystem && <Tab>{t('device.tenant')}</Tab>}  
          </TabList>

            <TabPanels>
              {/* Device Info Tab */}
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="bold">
                      {t('device.info')}
                    </Text>
                    {!isEditingDevice ? (
                      <IconButton
                        aria-label="Edit device"
                        icon={<FiEdit2 />}
                        size="sm"
                        colorScheme="blue"
                        onClick={() => setIsEditingDevice(true)}
                      />
                    ) : (
                      <HStack>
                        <IconButton
                          aria-label="Cancel edit"
                          icon={<FiX />}
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setIsEditingDevice(false);
                            resetDevice();
                            // Reset location data to original device values
                            if (device?.geo_lat && device?.geo_lng) {
                              setLocationData({
                                latitude: device.geo_lat,
                                longitude: device.geo_lng,
                                address: device.location || undefined,
                              });
                            } else {
                              setLocationData(null);
                            }
                          }}
                        />
                      </HStack>
                    )}
                  </HStack>

                  {isEditingDevice ? (
                    <form onSubmit={handleSubmitDevice(handleDeviceUpdate)}>
                      <VStack spacing={4}>
                        <FormControl isInvalid={!!deviceErrors.nick_name}>
                          <FormLabel>Device Name</FormLabel>
                          <Input
                            {...registerDevice('nick_name', {
                              required: 'Device name is required',
                            })}
                          />
                          <FormErrorMessage>
                            {deviceErrors.nick_name?.message}
                          </FormErrorMessage>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Description</FormLabel>
                          <Input {...registerDevice('description')} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Notes</FormLabel>
                          <Input {...registerDevice('notes')} />
                        </FormControl>

                        <FormControl>
                          <FormLabel>
                            <HStack>
                              <FiMapPin />
                              <Text>Device Location</Text>
                            </HStack>
                          </FormLabel>
                          <LocationPicker
                            value={locationData || undefined}
                            onChange={handleLocationChange}
                            placeholder="Search for device location or click on map"
                            height="250px"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Photo URL</FormLabel>
                          <Input {...registerDevice('photo')} />
                        </FormControl>

                        <Button
                          type="submit"
                          colorScheme="blue"
                          leftIcon={<FiSave />}
                          isLoading={isSaving}
                          alignSelf="flex-start"
                        >
                          Save Changes
                        </Button>
                      </VStack>
                    </form>
                  ) : (
                    <HStack>
                        <VStack align="stretch" spacing={3} width="50%">
                        <HStack>
                            <Text fontWeight="medium" minW="150px">Serial Number:</Text>
                            <Text>{device.serial_no}</Text>
                        </HStack>
                        <HStack>
                            <Text fontWeight="medium" minW="150px">Device Name:</Text>
                            <Text>{device.nick_name || 'N/A'}</Text>
                        </HStack>
                        { device.description && (
                          <HStack>
                            <Text fontWeight="medium" minW="150px">Description:</Text>
                            <Text>{device.description || 'N/A'}</Text>
                          </HStack>
                        )}
                        { device.notes && (
                          <HStack>
                            <Text fontWeight="medium" minW="150px">Notes:</Text>
                            <Text>{device.notes || 'N/A'}</Text>
                          </HStack>
                        )}
                        <HStack>
                            <Text fontWeight="medium" minW="150px">IMEI Number:</Text>
                            <Text>{device.imei_number || 'N/A'}</Text>
                        </HStack>
                        <HStack>
                            <Text fontWeight="medium" minW="150px">MAC Address:</Text>
                            <Text>{device.mac_address || 'N/A'}</Text>
                        </HStack>
                        <HStack>
                            <Text fontWeight="medium" minW="150px">GSM Signal:</Text>
                            <Text>{device.gsm_signal ? `${device.gsm_signal} dBm` : 'N/A'}</Text>
                        </HStack>
                        <HStack>
                            <Text fontWeight="medium" minW="150px">BLE Signal:</Text>
                            <Text>{device.ble_signal ? `${device.ble_signal} dBm` : 'N/A'}</Text>
                        </HStack>
                        <HStack>
                            <Text fontWeight="medium" minW="150px">Location:</Text>
                            <Text>{device.location || 'N/A'}</Text>
                        </HStack>
                        {(device.geo_lat && device.geo_lng) && (
                          <HStack>
                            <Text fontWeight="medium" minW="150px">GPS Coordinates:</Text>
                            <Badge colorScheme="blue" variant="subtle" fontFamily="monospace">
                              {parseFloat(device.geo_lat.toString()).toFixed(6)}, {parseFloat(device.geo_lng.toString()).toFixed(6)}
                            </Badge>
                          </HStack>
                        )}
                        <HStack>
                            <Text fontWeight="medium" minW="150px">Status:</Text>
                            <Badge colorScheme={getStatusColor(device.status)}>
                            {device.status}
                            </Badge>
                        </HStack>
                        <HStack>
                            <Text fontWeight="medium" minW="150px">Sensor:</Text>
                            <Badge colorScheme={device.sensor ? 'blue' : 'gray'}>
                            {device.sensor ? 'True' : 'False'}
                            </Badge>
                        </HStack>
                        <HStack>
                            <Text fontWeight="medium" minW="150px">Battery:</Text>
                            <Text>
                            {device.battery_voltage ? `${device.battery_voltage} mV` : 'N/A'}
                            </Text>
                        </HStack>
                        <HStack>
                            <Text fontWeight="medium" minW="150px">Last Seen:</Text>
                            <Text>
                            {device.last_connection_date
                                ? new Date(device.last_connection_date).toUTCString()
                                : 'N/A'}
                            </Text>
                        </HStack>
                        </VStack>
                        <VStack width="50%">
                        {device.photo && (
                            <Box style={{width: "100%", padding: "20px"}}>
                            {/* <Text fontWeight="medium" mb={2}>Photo:</Text> */}
                            <div style={{width: "300px", height: "200px", margin: "auto", overflow: "hidden", borderRadius: "10px"}}>
                                <Image src={device.photo} alt={device.nick_name || "Parking device"} style={{width: "100%", height: "100%", objectFit: "cover", objectPosition: "center"}} />
                            </div>
                            </Box>
                        )}
                        </VStack>
                    </HStack>
                  )}
                </VStack>
              </TabPanel>

              {/* QR Data Tab */}
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between">
                    <HStack spacing={2}>
                      <Text fontSize="lg" fontWeight="bold">
                        {t('device.qr_data')}
                      </Text>
                      <Badge colorScheme={device.qr_data ? 'green' : 'gray'} variant="subtle">
                        {device.qr_data ? t('device.qr_configured') : t('device.qr_not_configured')}
                      </Badge>
                    </HStack>
                    {!isEditingQrData ? (
                      <HStack spacing={2}>
                        <IconButton
                          aria-label={device.qr_data ? t('device.edit_qr_data') : t('device.add_qr_data')}
                          icon={device.qr_data ? <FiEdit2 /> : <FiUserPlus />}
                          size="sm"
                          colorScheme="blue"
                          onClick={() => {
                            resetQrData({
                              qr_data: device.qr_data || '',
                              device_code: device.device_code || '',
                            });
                            setIsEditingQrData(true);
                          }}
                        />
                        {device.qr_data && (
                          <IconButton
                            aria-label={t('device.delete_qr_data')}
                            icon={<FiTrash2 />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            isLoading={isSaving}
                            onClick={handleQrDataDelete}
                          />
                        )}
                      </HStack>
                    ) : (
                      <IconButton
                        aria-label={t('common.cancel')}
                        icon={<FiX />}
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setIsEditingQrData(false);
                          resetQrData();
                        }}
                      />
                    )}
                  </HStack>

                  {isEditingQrData ? (
                    <form onSubmit={handleSubmitQrData(handleQrDataSave)}>
                      <VStack spacing={4} align="stretch">
                        <FormControl isInvalid={!!qrDataErrors.qr_data}>
                          <FormLabel>{t('device.qr_data_value')}</FormLabel>
                          <Input
                            placeholder={t('device.enter_qr_data')}
                            {...registerQrData('qr_data', {
                              required: t('device.qr_data_required'),
                            })}
                          />
                          <FormErrorMessage>
                            {qrDataErrors.qr_data?.message}
                          </FormErrorMessage>
                        </FormControl>
                        
                        <FormControl isInvalid={!!qrDataErrors.device_code}>
                          <FormLabel>{t('device.spot_code')}</FormLabel>
                          <Input
                            placeholder={t('device.enter_spot_code')}
                            {...registerQrData('device_code')}
                          />
                          <FormErrorMessage>
                            {qrDataErrors.device_code?.message}
                          </FormErrorMessage>
                        </FormControl>

                        <HStack>
                          <Button
                            type="submit"
                            colorScheme="blue"
                            leftIcon={<FiSave />}
                            isLoading={isSaving}
                          >
                            {device.qr_data ? t('device.update_qr_data') : t('device.create_qr_data')}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setIsEditingQrData(false);
                              resetQrData();
                            }}
                          >
                            {t('common.cancel')}
                          </Button>
                        </HStack>
                      </VStack>
                    </form>
                  ) : (
                    <Box>
                      {device.qr_data ? (
                        <VStack align="stretch" spacing={3}>
                          <HStack>
                            <Text fontWeight="medium" minW="100px">{t('device.qr_data_value')}:</Text>
                            <Text fontFamily="monospace" bg="gray.50" px={2} py={1} borderRadius="md">
                              {device.qr_data}
                            </Text>
                          </HStack>
                          {device.device_code && (
                            <HStack>
                              <Text fontWeight="medium" minW="100px">{t('device.spot_code')}:</Text>
                              <Text fontFamily="monospace" bg="gray.50" px={2} py={1} borderRadius="md">
                                {device.device_code}
                              </Text>
                            </HStack>
                          )}
                        </VStack>
                      ) : (
                        <Alert status="info" borderRadius="md">
                          <AlertIcon />
                          <Box>
                            <Text fontWeight="medium">{t('device.no_qr_data')}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {t('device.click_to_add_qr_data')}
                            </Text>
                          </Box>
                        </Alert>
                      )}
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* Assigned Users Tab */}
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="lg" fontWeight="bold">
                    {t('device.user_management')}
                  </Text>

                  {/* Assign New User Form */}
                  <Box p={4} borderWidth="1px" borderRadius="lg">
                    <Text fontWeight="medium" mb={3}>
                      {t('device.assign_new_user')}
                    </Text>
                    <VStack spacing={3}>
                      <HStack spacing={3} width="100%">
                        <FormControl flex={2}>
                          <UserPicker
                            users={availableUsers.map(user => ({
                              id: user.id,
                              name: user.name,
                              surname: user.surname,
                              email: user.email,
                              phone_number: user.phone_number,
                              role: user.role,
                              photo: user.photo,
                            }))}
                            value={selectedUser}
                            onChange={handleUserSelect}
                            placeholder="Search users to assign to device..."
                            isLoading={isLoadingUsers}
                          />
                        </FormControl>
                        <FormControl flex={1} style={{ marginTop: '-20px' }}>
                          {/* <FormLabel>Relationship Type</FormLabel> */}
                          <Select
                            value={selectedRelationshipType}
                            onChange={(e) => setSelectedRelationshipType(e.target.value as RelationshipType)}
                          >
                            <option value={RelationshipType.OWNER}>Owner</option>
                            <option value={RelationshipType.ADMIN}>Admin</option>
                            <option value={RelationshipType.USERWITHSHARE}>User with Share</option>
                            <option value={RelationshipType.USER}>User</option>
                            <option value={RelationshipType.VISITOR}>Visitor</option>
                          </Select>
                        </FormControl>
                      </HStack>
                      <Button
                        colorScheme="blue"
                        leftIcon={<FiUserPlus />}
                        isLoading={isSaving}
                        isDisabled={!selectedUser}
                        onClick={handleAssignUser}
                        alignSelf="flex-start"
                      >
                        {t('device.assign_user')}
                      </Button>
                    </VStack>
                  </Box>

                  {/* Assigned Users List */}
                  <Box>
                    <Text fontWeight="medium" mb={3}>
                      {t('device.currently_assigned_users')}
                    </Text>
                    {isLoadingAssignedUsers ? (
                      <Box textAlign="center" py={4}>
                        <Spinner />
                      </Box>
                    ) : assignedUsers.length === 0 ? (
                      <Alert status="info">
                        <AlertIcon />
                        {t('device.no_users_assigned')}
                      </Alert>
                    ) : (
                      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>{t('user.name')}</Th>
                              <Th>{t('user.email')}</Th>
                              <Th>{t('user.phone_number')}</Th>
                              <Th>{t('user.relationship_type')}</Th>
                              <Th>{t('user.access_granted_at')}</Th>
                              <Th width="60px">Actions</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {assignedUsers.map((user) => (
                              <Tr key={user.id}>
                                <Td>{user.name || 'N/A'}</Td>
                                <Td>{user.email || 'N/A'}</Td>
                                <Td>{user.phone_number || 'N/A'}</Td>
                                <Td>
                                  <Badge colorScheme="blue" variant="subtle">
                                    {user.relationship_type || 'N/A'}
                                  </Badge>
                                </Td>
                                <Td>
                                  {user.access_granted_at
                                    ? new Date(user.access_granted_at).toLocaleDateString()
                                    : 'N/A'}
                                </Td>
                                <Td>
                                  <IconButton
                                    aria-label="Revoke access"
                                    icon={<FiTrash2 />}
                                    size="xs"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={() => handleRevokeUser(user.id)}
                                  />
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </Box>
                </VStack>
              </TabPanel>

              {/* Commands Tab */}
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="lg" fontWeight="bold">
                    {t('device.commands')}
                  </Text>
                  
                  <Text fontSize="sm" color="gray.600" mb={4}>
                    {t('device.send_commands_to_control_and_manage_the_device_remotely')}
                  </Text>

                  <VStack spacing={3} align="stretch">
                    <HStack spacing={4}>
                      <Button
                        leftIcon={<FiUnlock />}
                        colorScheme="green"
                        variant="outline"
                        isLoading={isSaving}
                        onClick={() => handleDeviceCommand('unblock')}
                        flex={1}
                      >
                        {t('device.unblock_device')}
                      </Button>
                      <Button
                        leftIcon={<FiLock />}
                        colorScheme="red"
                        variant="outline"
                        isLoading={isSaving}
                        onClick={() => handleDeviceCommand('block')}
                        flex={1}
                      >
                        {t('device.block_device')}
                      </Button>
                    </HStack>
                    
                    <HStack spacing={4}>
                      <Button
                        leftIcon={<FiInfo />}
                        colorScheme="blue"
                        variant="outline"
                        isLoading={isSaving}
                        onClick={() => handleDeviceCommand('info')}
                        flex={1}
                      >
                        {t('device.request_info')}
                      </Button>
                      <Button
                        leftIcon={<FiRefreshCw />}
                        colorScheme="orange"
                        variant="outline"
                        isLoading={isSaving}
                        onClick={() => handleDeviceCommand('reboot')}
                        flex={1}
                      >
                        {t('device.reboot_device')}
                      </Button>
                    </HStack>
                  </VStack>

                  <Alert status="warning" mt={4}>
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="medium">{t('common.important')}:</Text>
                      <Text fontSize="sm">
                        {t('device.device_commands_are_sent_immediately_and_cannot_be_undone')}
                      </Text>
                    </Box>
                  </Alert>
                </VStack>
              </TabPanel>
              {isSystem && (
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="lg" fontWeight="bold">
                    {t('device.tenant')}
                  </Text>

                  <Text>
                    {t('device.current_tenant')}:{' '}
                    <b>
                      {device.tenant_id
                        ? availableTenants.find(t => t.id === device.tenant_id)?.name ??
                          device.tenant_id
                        : t('device.no_tenant')}
                    </b>
                  </Text>

                  <HStack spacing={3}>
                    <FormControl flex={1}>
                      <Select
                        placeholder={t('device.select_tenant')}
                        value={selectedTenantId}
                        onChange={e => setSelectedTenantId(e.target.value)}
                        isDisabled={isLoadingTenants}
                      >
                        {availableTenants.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <Button
                      colorScheme="blue"
                      leftIcon={<FiSave />}
                      onClick={handleAssignTenant}
                      isDisabled={!selectedTenantId}
                      isLoading={isSaving}
                    >
                      {t('device.assign_tenant')}
                    </Button>
                  </HStack>
                </VStack>
              </TabPanel>
            )}
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeviceDetailsModal; 